import {TitleSummaryRequest} from '../../entity/model/title-summary-request';
import TitleSummaryRequestStore from '../store/title-summary-request-store';
import {TitleSummaryReportProps} from '../../entity/props/title/title-summary-report-props';
import {State} from '../../entity/model/state';
import {County} from '../../entity/model/county';
import {pdf} from '@react-pdf/renderer';
import React from 'react';
import TitleSummaryReportDocument from '../../app/layout/components/report/TitleSummaryReportDocument';
import { toast } from 'react-toastify';
import moment from 'moment';

export default class TitleSummaryRequestService {
  constructor(private titleSummaryRequestStore: TitleSummaryRequestStore) {}

  getTitleSummaryRequestDto = (): TitleSummaryRequest => {
    const props = this.titleSummaryRequestStore.currentProps;

    return {
      _id: props?.reportData?._id ?? crypto.randomUUID(),
      orderInformation: {
        orderNumber: props?.reportData?.orderInformation?.orderNumber ?? props?.project?.orderNumber,
        searchType: props?.reportData?.orderInformation?.searchType ?? props?.project?.searchType,
        searchStartDate: props?.reportData?.orderInformation?.searchStartDate,
        searchEndDate: props?.reportData?.orderInformation?.searchEndDate
      },
      propertyInformation: {
        stateID: props?.reportData?.propertyInformation?.stateID ?? props?.title?.stateID ?? props?.project?.stateID,
        countyID: props?.reportData?.propertyInformation?.countyID ?? props?.title?.countyID ?? props?.project?.countyID,
        propertyAddress: props?.reportData?.propertyInformation?.propertyAddress ?? props?.title?.address ?? props?.project?.address,
        parcelNumber: props?.reportData?.propertyInformation?.parcelNumber ?? props?.title?.tmsParcelNumbers?.[0],
        currentOwner: props?.reportData?.propertyInformation?.currentOwner ?? props?.project?.sellers
      },
      vestingDeeds: props?.reportData?.vestingDeeds ?? [],
      estates: props?.reportData?.estates ?? [],
      taxYears: props?.reportData?.taxYears ?? [],
      taxFlags: {
        pendingTaxSale: props?.reportData?.taxFlags?.pendingTaxSale ?? false,
        delinquentTaxes: props?.reportData?.taxFlags?.delinquentTaxes ?? false,
        rollBackTaxes: props?.reportData?.taxFlags?.rollBackTaxes ?? false,
        taxExempt: props?.reportData?.taxFlags?.taxExempt ?? false
      },
      mobileHome: props?.reportData?.mobileHome ?? [],
      mortgages: props?.reportData?.mortgages ?? [],
      judgments: props?.reportData?.judgments ?? [],
      liens: props?.reportData?.liens ?? [],
      uccs: props?.reportData?.uccs ?? [],
      restrictionsAgreements: props?.reportData?.restrictionsAgreements ?? [],
      easementsRightOfWays: props?.reportData?.easementsRightOfWays ?? [],
      comment: props?.reportData?.comment
    };
  }

  loadTitleSummaryRequests = async (organizationId: string, titleId: string): Promise<void> => {
    await this.titleSummaryRequestStore.loadTitleSummaryRequests(organizationId, titleId);
  }

  getTitleSummaryRequest = (documentId: string): TitleSummaryRequest | undefined => {
    return this.titleSummaryRequestStore.titleSummaryRequests.find((titleSummaryRequest: TitleSummaryRequest) => titleSummaryRequest.documentID === documentId);
  }

  loadSharedData = (): void => {
    this.titleSummaryRequestStore.loadSharedData();
  }

  getCurrentStateId = (): string => {
    const props = this.titleSummaryRequestStore.currentProps;
    return (
      props?.reportData?.propertyInformation?.stateID ??
      props?.title?.stateID ??
      props?.project?.stateID ??
      ''
    );
  }

  getCurrentProps = (): TitleSummaryReportProps | undefined => {
    return this.titleSummaryRequestStore.currentProps;
  }

  setCurrentProps = (props: TitleSummaryReportProps | undefined): void => {
    this.titleSummaryRequestStore.setCurrentProps(props);
  }

  generateTitleSummaryReportPdf = async (titleSummaryRequest: TitleSummaryRequest, state?: State, county?: County): Promise<Blob> => {
    try {
      return await pdf(
                React.createElement(TitleSummaryReportDocument, {
                  titleSummaryRequest: titleSummaryRequest,
                  state: state,
                  county: county
                }) as any
      ).toBlob();
    } catch (error) {
      console.error('Error generating PDF report:', error);
      throw error;
    }
  }

  createTitleSummaryRequest = async (organizationId: string, titleSummaryRequest: TitleSummaryRequest): Promise<void> => {
    const result = await this.titleSummaryRequestStore.createTitleSummaryRequest(organizationId, { body: titleSummaryRequest });
    if (typeof result === 'string')
      toast.error(result);
    else
      toast.success('Title summary report uploaded successfully!');
  }

  updateTitleSummaryRequest = async (organizationId: string, titleSummaryRequestId: string, titleSummaryRequest: TitleSummaryRequest): Promise<void> => {
    const result = await this.titleSummaryRequestStore.updateTitleSummaryRequest(organizationId, titleSummaryRequestId, { body: titleSummaryRequest });
    if (typeof result === 'string')
      toast.error(result);
    else
      toast.success('Title summary report updated successfully!');
  }

  getSanitizeTitleSummaryRequest = (titleSummaryRequest: TitleSummaryRequest, titleId?: string): TitleSummaryRequest => {
    return {
      ...titleSummaryRequest,
      orderInformation: {
        ...titleSummaryRequest.orderInformation,
        searchStartDate: titleSummaryRequest.orderInformation?.searchStartDate ? moment(titleSummaryRequest.orderInformation.searchStartDate).utc().toISOString() : undefined,
        searchEndDate: titleSummaryRequest.orderInformation?.searchEndDate ? moment(titleSummaryRequest.orderInformation.searchEndDate).utc().toISOString() : undefined
      },
      vestingDeeds: titleSummaryRequest.vestingDeeds?.map(vestingDeed => ({
        ...vestingDeed,
        recordedDate: vestingDeed?.recordedDate ? moment(vestingDeed.recordedDate).utc().toISOString() : undefined
      })),
      mobileHome: titleSummaryRequest.mobileHome?.map(mobileHome => ({
        ...mobileHome,
        lienRetirement: {
          ...mobileHome.lienRetirement,
          recordedDate: mobileHome.lienRetirement?.recordedDate ? moment(mobileHome.lienRetirement.recordedDate).utc().toISOString() : undefined
        }
      })),
      mortgages: titleSummaryRequest.mortgages?.map(mortgage => ({
        ...mortgage,
        recordedDate: mortgage?.recordedDate ? moment(mortgage.recordedDate).utc().toISOString() : undefined,
        assignments: mortgage?.assignments?.map(assignment => ({
          ...assignment,
          recordedDate: assignment.recordedDate ? moment(assignment.recordedDate).utc().toISOString() : undefined
        }))
      })),
      judgments: titleSummaryRequest.judgments?.map(judgement => ({
        ...judgement,
        recordedDate: judgement?.recordedDate ? moment(judgement.recordedDate).utc().toISOString() : undefined
      })),
      liens: titleSummaryRequest.liens?.map(lien => ({
        ...lien,
        recordedDate: lien?.recordedDate ? moment(lien.recordedDate).utc().toISOString() : undefined
      })),
      restrictionsAgreements: titleSummaryRequest.restrictionsAgreements?.map(agreement => ({
        ...agreement,
        recordedDate: agreement?.recordedDate ? moment(agreement.recordedDate).utc().toISOString() : undefined
      })),
      easementsRightOfWays: titleSummaryRequest.easementsRightOfWays?.map(easement => ({
        ...easement,
        recordedDate: easement?.recordedDate ? moment(easement.recordedDate).utc().toISOString() : undefined
      })),
      uccs: titleSummaryRequest.uccs?.map(ucc => ({
        ...ucc,
        filingDate: ucc?.filingDate ? moment(ucc.filingDate).utc().toISOString() : undefined
      })),
      projectTitleID: titleId ?? ''
    };
  }
}
